import {performRestCallDirectly} from "./HttpHelper";
import {Dispatch} from "redux";
import {AccountsState} from "../state/AccountState";
import {PgpKeyDto} from "../services/messages/PgpKeyDto";
import {getKeyFirstUserId, getKeyHexId, PgpKey} from "../domain/PgpKey";
import {AccountsActions} from "../redux/AccountsSlice";
import {DB} from "../db/DbManager";
import {convertTextToTitleCase} from "./Formatters";
import * as openpgp from 'openpgp';
import {enrichPgpKey} from "../security/PgpKeyEnricher";

// See here for OpenPGPJS documentation: https://openpgpjs.org/openpgpjs/

export type PasswordGenerationVariant = "PIN" | "password";

export async function getPgpSigningKey(signingKeys: PgpKey[], passphrase: string): Promise<openpgp.PrivateKey> {
    const decryptedKey = await openpgp.decryptKey({privateKey: signingKeys[0].key as openpgp.PrivateKey, passphrase});
    return (await decryptedKey.getSigningKey()) as openpgp.PrivateKey;
}

export function pgpReadPrivateKey(options: { armoredKey: string }): Promise<openpgp.Key> {
    return openpgp.readKey(options);
}

export function pgpReadKey(options: { armoredKey: string, checksumRequired?: boolean }): Promise<openpgp.Key> {
    return openpgp.readKey(options);
}

export function pgpReadArmoredMessage(message: string): Promise<openpgp.Message<string>> {
    return openpgp.readMessage({armoredMessage: message});
}

export function pgpReadSignature(signature: string): Promise<openpgp.Signature> {
    return openpgp.readSignature({armoredSignature: signature});
}

export function pgpReadCleartextMessage(message: string): Promise<openpgp.CleartextMessage> {
    return openpgp.readCleartextMessage({cleartextMessage: message});
}

export function pgpReadBinaryMessage(message: Uint8Array): Promise<openpgp.Message<Uint8Array>> {
    return openpgp.readMessage({binaryMessage: message});
}

export function pgpTextMessage(text: string): Promise<openpgp.Message<string>> {
    return openpgp.createMessage({text});
}

export function pgpDecrypt<T extends string | Uint8Array>(message: openpgp.Message<T>, decryptionKeys: openpgp.PrivateKey | openpgp.PrivateKey[] | undefined, verificationKeys?: openpgp.PublicKey | openpgp.PublicKey[], format?: "binary" | "utf8") {
    return openpgp.decrypt({
        message,
        decryptionKeys,
        verificationKeys: verificationKeys,
        format,
    });
}

export function pgpVerify<T extends string | Uint8Array>(message: openpgp.Message<T>, verificationKeys: openpgp.PublicKey | openpgp.PublicKey[], detachedSignature: openpgp.Signature | undefined) {
    return openpgp.verify({
        message,
        // expectSigned: false,
        verificationKeys: verificationKeys,
        signature: detachedSignature,
    });
}

export async function pgpEncrypt(format: "armored", message: openpgp.Message<string>, encryptionKeys: openpgp.Key | openpgp.Key[]): Promise<string>;
export async function pgpEncrypt(format: "binary", message: openpgp.Message<Uint8Array>, encryptionKeys: openpgp.Key | openpgp.Key[]): Promise<Uint8Array>;
export async function pgpEncrypt<T extends string | Uint8Array>(format: "armored" | "binary", message: openpgp.Message<T>, encryptionKeys: openpgp.Key | openpgp.Key[]): Promise<T> {
    const encryptedData = await openpgp.encrypt({
        message,
        encryptionKeys,
        config: {preferredCompressionAlgorithm: openpgp.enums.compression.zip},
        format: format as any,
    });
    return encryptedData as unknown as T;
}

export async function pgpSign(message: openpgp.Message<any>, signingKey: openpgp.PrivateKey, detached: boolean) {
    const signatureData = await openpgp.sign({
        message,
        detached,
        signingKeys: signingKey,
        format: "armored",
    });
    return signatureData as unknown as string;
}

export async function pgpSignAndEncrypt(message: openpgp.Message<any>, encryptionKeys: openpgp.PublicKey | openpgp.PublicKey[], signingKey: openpgp.PrivateKey) {
    const encryptedData = await openpgp.encrypt({
        message,
        encryptionKeys,
        signingKeys: signingKey,
        config: {preferredCompressionAlgorithm: openpgp.enums.compression.zip},
        format: "armored",
    });
    return encryptedData as unknown as string;
}

export async function pgpSignKey(keyToSign: openpgp.PublicKey, signingKey: openpgp.PrivateKey) {
    return keyToSign.signAllUsers([signingKey]);
}

export function pgpReadTextSignature(signature: string) {
    return openpgp.readSignature({armoredSignature: signature});
}

export function pgpReadBinarySignature(signature: Uint8Array) {
    return openpgp.readSignature({binarySignature: signature});
}

// *********************

export function findPgpKey(keyId: openpgp.KeyID | undefined, allKeys: PgpKey[]): PgpKey | undefined {
    if (!keyId) return;
    return allKeys
        .find(k => k.key.getKeyIDs()
            .some(k => k.bytes === keyId.bytes));
}

export function findPgpKeyByHex(keyId: string | undefined, allKeys: PgpKey[]): PgpKey | undefined {
    if (!keyId) return;
    return allKeys.find(k => k.keyHexId === keyId);
}

const validPasswordChars = [
    "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
    "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z",
    "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m",
    "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z",
    "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "/", "!",
    "£", "$", "%", "^", "&", "*", "(", ")", "-", "=", "[", "]", "#",
    "@", ",", ".", "<", ">", "?", "_"
];

const validPinChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

export async function ensurePgpKeysLoadedBoolean(dispatch: Dispatch, pgpKeysLoaded: boolean) {
    try {
        if (!pgpKeysLoaded) {
            const encodedKeys = await DB.getAllPgpKeys();
            const keys = await decodePgpKeyDtos(encodedKeys, []) || [];

            for (const key of keys) {
                key.enrichedKey = await enrichPgpKey(key, keys);
            }

            dispatch(AccountsActions.setPgpKeys({keys, loadedIfEmpty: true}));
        }
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function ensurePgpKeysLoaded(dispatch: Dispatch, accountsState: AccountsState) {
    return ensurePgpKeysLoadedBoolean(dispatch, accountsState.pgpKeysLoaded);
}

export function findDecryptionKeys(message: openpgp.Message<any>, secretKeys: PgpKey[]): PgpKey[] {
    const potentialKeys: PgpKey[] = [];

    try {
        const potentialKeyIds = message.getEncryptionKeyIDs();

        for (const secretKey of secretKeys) {
            for (const keyId of secretKey.key.getKeyIDs()) {

                if (potentialKeyIds.some((p: any) => p.bytes === keyId.bytes)) {
                    potentialKeys.push(secretKey);
                }
            }
        }
    } catch (e) {
        console.error(e);
    }

    if (potentialKeys.length > 0) {
        console.log(`Found potential keys: ${potentialKeys.map(k => k.keyHexId).join(", ")}`);
    }
    return potentialKeys;
}

export async function decryptSecretKeys(secretKeys: PgpKey[], password: string) {
    const decryptedKeys: openpgp.PrivateKey[] = [];
    let error: any = null;

    for (const secretKey of secretKeys) {
        try {
            if (!(secretKey.key as openpgp.PrivateKey).isDecrypted()) {
                const privateKey = await openpgp.decryptKey({privateKey: secretKey.key as openpgp.PrivateKey, passphrase: password});

                if (privateKey.isDecrypted()) {
                    decryptedKeys.push(privateKey);
                }
            }
        } catch (e) {
            error = e;
        }
    }

    if (decryptedKeys.length === 0 && error) {
        throw error;
    } else if (decryptedKeys.length === 0) {
        throw new Error("You have no private keys");
    }

    return decryptedKeys;
}

export async function getSecretKeysFromWebService() {
    const results = await performRestCallDirectly<PgpKeyDto[]>("GetPgpPrivateKeys", {});

    await DB.saveSecretKeys(results);

    return results;
}

export interface PasswordGenerationOptions {
    length: number;
    requireNumbers?: boolean;
    requireSymbols?: boolean;
    excludeNumbers?: boolean;
    excludeSymbols?: boolean;
    variant: PasswordGenerationVariant;
    useWords?: boolean;
    availableWords?: string[];
}

async function getRandomBytes(length: number): Promise<Uint8Array> {
    const result = new Uint8Array(length);
    crypto.getRandomValues(result);
    return result;
}

export async function generateRandomPassword(options: PasswordGenerationOptions) {
    let result = "";
    let count = 0;

    const passwordValidCharsLength = options.excludeSymbols && options.excludeNumbers ?
        26 * 2
        : (options.excludeSymbols ? 26 * 2 + 10 : validPasswordChars.length);

    const pinValidCharsLength = 10;
    const validCharsLength = options.variant === "password" ? passwordValidCharsLength : pinValidCharsLength;

    const validChars = options.variant === "password" ? validPasswordChars : validPinChars;

    do {
        const randomData = await getRandomBytes(options.useWords ? options.length * 4 : options.length);
        let temp = "";
        let number = 0;
        let offset = 0;
        for (const data of randomData) {
            if (options.useWords && options.availableWords && offset < 32) {
                if (offset === 24) {
                    let randomWord = options.availableWords[number % options.availableWords.length];
                    if ((number + data) % 2 === 0) {
                        randomWord = convertTextToTitleCase(randomWord);
                    }
                    temp += randomWord;
                } else {
                    number += (data << offset);
                }
                offset += 8;
                if (temp.length >= options.length) {
                    break;
                }
                continue;
            } else if (options.excludeNumbers && !options.excludeSymbols) {
                const normalisedData = data % validCharsLength;
                if (normalisedData >= 26 * 2 && normalisedData < 26 * 2 + 10) {
                    temp += validChars[data % (26 * 2)];
                } else {
                    temp += validChars[data % validCharsLength];
                }
            } else {
                temp += validChars[data % validCharsLength];
            }
            number = 0;
            offset = 0;
        }
        result = temp;

        if (passwordRequirementsMet(options, result)) {
            console.info(`Password generated in ${count + 1} attempts`);
            return result;
        }
    } while (count++ < 100);

    console.warn("Exceeded maximum password generation attempts");

    return result;
}

export function passwordRequirementsMet(options: PasswordGenerationOptions, text: string): boolean {
    if (options.requireNumbers) {
        if (!/[0-9]/.test(text)) {
            return false;
        }
    }

    if (options.requireSymbols) {
        if (!/[^0-9a-zA-Z]/.test(text)) {
            return false;
        }
    }

    return true;
}

export async function decodePgpKeyDtos(keyDtos: PgpKeyDto[] | null, allKeys: PgpKey[]): Promise<PgpKey[] | null> {
    if (!keyDtos) return null;

    const results: PgpKey[] = [];
    for (const dto of keyDtos) {
        try {
            const decodedKey = await pgpReadKey({armoredKey: dto.Key});
            const pgpKey: PgpKey = {
                key: decodedKey,
                keyHexId: getKeyHexId(decodedKey),
                isPrivate: decodedKey.isPrivate(),
                Id: dto.Id,
            };
            pgpKey.enrichedKey = await enrichPgpKey(pgpKey, allKeys);
            results.push(pgpKey);
        } catch (error: any) {
            console.info("Unable to decode key", dto.Id, error.message);
        }
    }
    return results;
}

export function getKeyDescriptions(keys: PgpKey[]): string {
    if (!keys.length) {
        return "No keys";
    }
    return keys.map(getKeyFirstUserId).join(", ");
}
